/**
 * Fait : fixe le CONTRAT entre l'API et le worker d'extraction — ce que l'API
 *   demande (un fichier, son format, les bornes à respecter) et ce que le
 *   worker répond (le texte et le nombre de pages, ou un échec NOMMÉ). Écrit
 *   une seule fois, pour que les deux côtés ne puissent pas diverger.
 *
 * Ne fait pas : il n'extrait rien, ne lance aucun processus et ne connaît ni
 *   la base ni le canal. Les natures d'échec sont ici, mais ce qu'on en fait
 *   — refuser le fichier, le dire à l'écran — appartient à l'API.
 */

import { z } from 'zod';

/**
 * Les natures d'échec d'une extraction. Nommées, closes, et distinctes : un
 * refus qui ne dit pas POURQUOI oblige l'écran à mentir ou à se taire.
 */
export const NATURES_D_ECHEC_D_EXTRACTION = [
  /** Le fichier dépasse la taille admise. */
  'trop_gros',
  /** Le document a plus de pages que la limite. */
  'trop_de_pages',
  /** Illisible, tronqué, ou pas du format annoncé. */
  'illisible',
  /** Le travail a dépassé son délai — il est tué, pas attendu. */
  'delai_depasse',
  /** Le worker a manqué de mémoire, ou s'est arrêté sans rien dire. */
  'worker_perdu',
  /** Le worker a tenté de sortir sur le réseau : il n'en a pas le droit. */
  'sortie_reseau',
] as const;

export type NatureDEchecDExtraction = (typeof NATURES_D_ECHEC_D_EXTRACTION)[number];

/** Ce que l'API demande au worker. Tout est borné, rien n'est deviné. */
export const demandeDExtraction = z
  .object({
    /** Chemin ABSOLU du fichier à lire — le worker n'en cherche aucun autre. */
    chemin: z.string().min(1),

    format: z.enum(['pdf', 'txt', 'markdown', 'docx']),
    tailleMaximaleOctets: z.number().int().positive(),
    pagesMaximales: z.number().int().positive(),
  })
  .strict();

export type DemandeDExtraction = z.infer<typeof demandeDExtraction>;

/** Ce que le worker répond — une réussite, ou un échec nommé. Jamais les deux. */
export const reponseDExtraction = z.discriminatedUnion('issue', [
  z
    .object({
      issue: z.literal('extrait'),
      texte: z.string(),

      /** Le nombre de pages RÉEL du document, une fois ouvert. */
      pages: z.number().int().nonnegative(),
    })
    .strict(),
  z
    .object({
      issue: z.literal('echec'),
      nature: z.enum(NATURES_D_ECHEC_D_EXTRACTION),

      /**
       * Une explication courte, DESTINÉE AUX JOURNAUX. Elle ne porte jamais
       * le contenu du document : un extrait de texte dans un journal serait
       * une fuite (critère 30).
       */
      detail: z.string().max(300),
    })
    .strict(),
]);

export type ReponseDExtraction = z.infer<typeof reponseDExtraction>;
